import { Box, Container, Flex, Heading, Stack, Text } from '@chakra-ui/react';
import { BackLink } from './BackLink'
import type { BackTarget } from '../../lib/returnNav'

interface PageContainerProps {
  title?: React.ReactNode
  subtitle?: React.ReactNode
  actions?: React.ReactNode
  /** LW-9: when set, renders a top-left back link above the title. */
  back?: BackTarget | null
  maxW?: string
  /** Passed through to the underlying Container -- lets a page (e.g. a print stylesheet's
   * `.report-root` rule) target the element that actually carries the page's max-width and
   * side padding, rather than an untagged wrapper Box around it. */
  className?: string
  children: React.ReactNode
}

export function PageContainer({ title, subtitle, actions, back, maxW = 'container.xl', className, children }: PageContainerProps) {
  return (
    <Container className={className} maxW={maxW} px={{ base: 4, md: 8 }} py={{ base: 4, md: 8 }}>
      {back && (
        <Box mb={4}>
          <BackLink to={back.to} label={back.label} />
        </Box>
      )}
      {(title || actions) && (
        <Flex
          direction={{ base: 'column', md: 'row' }}
          align={{ base: 'stretch', md: 'center' }}
          justify="space-between"
          gap={4}
          mb={6}
        >
          <Box>
            {title && <Heading size="lg">{title}</Heading>}
            {subtitle && <Text color="muted" mt={1}>{subtitle}</Text>}
          </Box>
          {actions && (
            <Stack
              direction={{ base: 'column', md: 'row' }}
              gap={3}
              w={{ base: 'full', md: 'auto' }}
              css={{
                '& > *': { width: { base: '100%', md: 'auto' } }
              }}
            >
              {actions}
            </Stack>
          )}
        </Flex>
      )}
      {children}
    </Container>
  );
}
